import React from 'react';

// 인스타그램 스크래퍼 플러그인(MoScraper) 가이드 — 화면 설치 가이드와 같은 급의
// 일반 사용자용 문서 페이지. 크롬 zip 은 MoScraper/package.sh, 파이어폭스 .xpi(AMO
// 서명본)는 MoScraper/sign.sh 가 static/plugin/ 에 생성.

const Step: React.FC<{ n: number; children: React.ReactNode }> = ({ n, children }) => (
  <li className="flex gap-2 text-sm text-gray-700">
    <span className="flex-none w-5 h-5 rounded-full bg-gray-200 text-gray-700 text-xs flex items-center justify-center">{n}</span>
    <span>{children}</span>
  </li>
);

const SECTIONS = [
  { id: 'what', title: '무엇을 하는 플러그인인가' },
  { id: 'install', title: '설치' },
  { id: 'use', title: '사용법' },
  { id: 'auto', title: '자동 입력이 되는 것들' },
  { id: 'owner', title: '페이지 주인(owner)이 미리 해둘 설정' },
];

const PluginGuide: React.FC = () => (
  <div className="p-4 md:p-6 max-w-4xl">
    <h1 className="text-2xl font-bold mb-1">인스타그램 플러그인 가이드</h1>
    <p className="text-sm text-gray-500 mb-6">
      인스타그램 게시물의 영상과 본문을 한 번의 클릭으로 MOc 화면에 올리는 브라우저 확장(MoScraper)입니다.
    </p>

    <nav className="flex flex-wrap gap-2 mb-6">
      {SECTIONS.map((s) => (
        <a key={s.id} href={`#${s.id}`} className="text-xs px-2.5 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded">
          {s.title}
        </a>
      ))}
    </nav>

    <section id="what" className="mb-8 scroll-mt-4">
      <h2 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-1 mb-2">무엇을 하는 플러그인인가</h2>
      <p className="text-sm text-gray-700 mb-2">
        인스타그램 게시물 페이지에서 툴바 버튼을 누르면, 게시물의 <strong>영상(영상이 없으면 첫 사진)</strong>과
        본문을 자동으로 읽어 제목·행사 일시·홍보 기간을 채워 줍니다. 확인 후 업로드하면
        <strong> 비로그인 업로드</strong>로 접수되고, 페이지 주인이 승인하면 화면에 게시됩니다.
      </p>
      <p className="text-xs text-gray-400">MOc 로그인은 필요 없습니다 — 업로드 대상 페이지의 handle 과 업로드 코드만 있으면 됩니다.</p>
    </section>

    <section id="install" className="mb-8 scroll-mt-4">
      <h2 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-1 mb-2">설치</h2>
      <div className="flex flex-wrap gap-2 mb-4">
        <a href="/static/plugin/moscraper-firefox.xpi"
          className="px-3 py-2 bg-orange-500 text-white text-sm rounded hover:bg-orange-600">파이어폭스용 설치 (.xpi)</a>
        <a href="/static/plugin/moscraper-chrome.zip" download
          className="px-3 py-2 bg-blue-500 text-white text-sm rounded hover:bg-blue-600">크롬용 다운로드 (.zip)</a>
      </div>

      <h3 className="text-sm font-semibold text-gray-700 mb-1">파이어폭스 (권장 — 클릭 한 번으로 설치)</h3>
      <ol className="space-y-1.5 mb-4">
        <Step n={1}><strong>파이어폭스에서</strong> 위 설치 버튼을 누릅니다.</Step>
        <Step n={2}>"설치를 허용하시겠습니까?" 알림에서 <strong>계속 → 추가</strong>를 누르면 끝. 재시작해도 유지됩니다 (Mozilla 서명본).</Step>
        <Step n={3}>알림 없이 파일로 받아졌다면: <code className="bg-gray-100 px-1 rounded">about:addons</code> → 톱니 → <strong>파일에서 부가 기능 설치</strong> → 받은 .xpi 선택.</Step>
      </ol>
      <p className="text-xs text-gray-500 mb-4">
        한 번 설치하면 이후 새 버전은 파이어폭스가 <strong>자동으로 업데이트</strong>합니다 (v0.2.3부터).
        이전 버전을 쓰고 있다면 위 버튼으로 한 번만 다시 설치하세요.
      </p>

      <h3 className="text-sm font-semibold text-gray-700 mb-1">크롬</h3>
      <ol className="space-y-1.5">
        <Step n={1}>zip 을 다운로드해 <strong>압축 해제</strong>합니다 (폴더는 지우지 말고 보관).</Step>
        <Step n={2}>주소창에 <code className="bg-gray-100 px-1 rounded">chrome://extensions</code> 입력 → 우상단 <strong>개발자 모드</strong> 켜기.</Step>
        <Step n={3}><strong>압축해제된 확장 프로그램을 로드합니다</strong> → 압축 푼 폴더 선택.</Step>
        <Step n={4}>퍼즐 아이콘에서 MoC 로고를 툴바에 고정하면 편합니다. 브라우저를 재시작해도 유지됩니다.</Step>
      </ol>
    </section>

    <section id="use" className="mb-8 scroll-mt-4">
      <h2 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-1 mb-2">사용법</h2>
      <ol className="space-y-1.5 mb-3">
        <Step n={1}>인스타그램에서 올리고 싶은 <strong>게시물 페이지</strong>(/p/… 또는 /reel/…)를 엽니다 (로그인 상태).</Step>
        <Step n={2}>툴바의 <strong>MoC 버튼</strong>을 누르면 영상·본문·행사 일시가 자동으로 채워집니다.</Step>
        <Step n={3}>업로드 페이지 <strong>handle</strong> 과 <strong>업로드 코드</strong>를 입력합니다 (한 번 입력하면 기억됩니다).</Step>
        <Step n={4}>채워진 값을 확인·수정하고 <strong>업로드</strong>. 팝업을 닫아도 업로드는 계속됩니다.</Step>
        <Step n={5}>페이지 주인이 대시보드 "업로드 승인 대기"에서 승인하면 화면에 게시됩니다.</Step>
      </ol>
      <p className="text-xs text-gray-400">
        팁: 입력 중 팝업이 닫혀도 같은 게시물에서 다시 열면 <strong>작성 중이던 값이 복원</strong>됩니다.
        오래 편집할 때는 "별창" 버튼으로 분리 창을 쓰세요 (파이어폭스에선 달력 선택도 별창이 편합니다).
      </p>
    </section>

    <section id="auto" className="mb-8 scroll-mt-4">
      <h2 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-1 mb-2">자동 입력이 되는 것들</h2>
      <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700">
        <li><strong>제목</strong> = 본문 첫 줄.</li>
        <li><strong>행사 시작</strong> = 본문에서 처음 발견되는 날짜·시각 (예: 8월 15일 저녁 7시, 8/15 19:00, 26.8.23).
          시각이 없으면 <strong>22:00</strong>으로 넣고 알려줍니다.</li>
        <li><strong>종료</strong> = 본문에 범위(19:00~21:00, 8/15~17)가 있으면 그 값, 없으면 시작+1시간.</li>
        <li><strong>홍보 시작</strong> = 행사 N일 전 (기본 1주). 직접 바꾸면 그 운영처의 다음 기본값으로 기억됩니다.</li>
        <li><strong>handle</strong> = 게시물 작성자(또는 로그인 계정)의 인스타 아이디가 MOc 에 등록돼 있으면 자동 선택.</li>
        <li>사진 게시물은 서버가 <strong>영상으로 변환</strong>해 게시합니다 (재생 길이·너비 조절 가능).</li>
      </ul>
    </section>

    <section id="owner" className="mb-8 scroll-mt-4">
      <h2 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-1 mb-2">페이지 주인(owner)이 미리 해둘 설정</h2>
      <p className="text-sm text-gray-700 mb-2">
        프로필 → <strong>비로그인 업로드 페이지</strong> 섹션에서:
      </p>
      <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700">
        <li><strong>handle</strong> — 공개 업로드 주소(/@handle). 비우면 페이지가 비공개됩니다.</li>
        <li><strong>업로드 코드</strong> — 업로더에게 알려줄 코드. 미설정 시 기본값은 이름 대문자.</li>
        <li><strong>인스타그램 아이디</strong> — 등록하면 플러그인이 이 계정의 게시물에서 내 페이지를 자동 선택합니다.</li>
      </ul>
    </section>
  </div>
);

export default PluginGuide;
